import type { ReactNode } from "react";
import { useMemo } from "react";

import dayjs from "dayjs";

import { useTranslate } from "@similarweb/i18n-provider";
import { Month, Text as TextIcon } from "@similarweb/icons";
import { fullMonthDayAndYear, shortMonthAndDay } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { useTracking } from "@similarweb/tracking-provider";
import { Tooltip, TruncatedTextWithTooltip } from "@similarweb/ui-components";
import { Flex, Stack } from "@similarweb/ui-components/foundations";

import { ensureHttpsPrefix } from "../../../utils";
import { Link } from "../../common/card-layout/Link";
import { InfoItem } from "../../common/InfoItem";
import { OpenInNewTab } from "../../common/OpenInNewTab";
import { BorderCard, FooterWrapper } from "../../common/styledComponents";

export const adCardSize = {
    width: 319,
    height: 200,
};

type SearchAdCardProps = {
    description: ReactNode;
    firstSeen: string;
    landingPage: string;
    title: ReactNode;
};

export const SearchAdCard = ({ description, firstSeen, landingPage, title }: SearchAdCardProps) => {
    const { translate } = useTranslate();
    const { track } = useTracking();

    const { shortDate, fullDate } = useMemo(() => {
        const date = dayjs(firstSeen);

        return {
            shortDate: date.format(shortMonthAndDay),
            fullDate: date.format(fullMonthDayAndYear),
        };
    }, [firstSeen]);

    const handleLandingPageOpen = () => {
        track("Alert Card", "click", "open landingPage alert/alerts/search-ads");
    };

    const handleTitleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        handleLandingPageOpen();
    };

    return (
        <BorderCard
            data-automation="search-ad-card"
            style={{ width: adCardSize.width, height: adCardSize.height }}
        >
            <Stack
                flexGrow={1}
                padding="16px 16px 0"
                gap="4px"
            >
                <Stack gap="4px">
                    <TruncatedTextWithTooltip
                        lineClamp={2}
                        fontVariant="subTitle3Alt"
                        tooltipProps={{ content: title }}
                    >
                        <Link
                            color={colorsPalettes.blue[500]}
                            fontVariant="subTitle3Alt"
                            href={ensureHttpsPrefix(landingPage)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={handleTitleClick}
                        >
                            {title}
                        </Link>
                    </TruncatedTextWithTooltip>
                    <TruncatedTextWithTooltip
                        lineClamp={4}
                        fontVariant="body2"
                        color={colorsPalettes.carbon[400]}
                    >
                        {description}
                    </TruncatedTextWithTooltip>
                </Stack>
            </Stack>
            <FooterWrapper data-automation="ad-card-footer">
                <Flex gap="12px">
                    <InfoItem
                        icon={TextIcon}
                        title="Text"
                        tooltip={translate("activation.alerts.cards.social-post.creative-type", { type: "Text" })}
                    />
                    <InfoItem
                        icon={Month}
                        title={shortDate}
                        tooltip={translate("activation.alerts.cards.social-post.first-seen", {
                            date: fullDate,
                        })}
                    />
                </Flex>
                <Tooltip content={translate("activation.alerts.cards.ads.landing-page")}>
                    <OpenInNewTab
                        url={landingPage}
                        data-automation="open-landing-page-button"
                        onOpen={handleLandingPageOpen}
                    />
                </Tooltip>
            </FooterWrapper>
        </BorderCard>
    );
};
